export const environment = import.meta.env.VITE_ENVIRONMENT;

export const isLocal = environment === 'local';
export const isProduction = environment === 'production';
export const isDevelopment = environment === 'development';

// Opt-in (per developer, via `VITE_APP_ENABLE_LOCAL_TELEMETRY=true` in `.env`) to
// ship ambient telemetry — Faro RUM, Mixpanel, OpenPanel — and signals from a
// LOCAL build, which otherwise stubs to the console. Makes debugging the full
// pipeline locally easy. No effect on development/production (they always ship).
const localTelemetryEnabled = import.meta.env.VITE_APP_ENABLE_LOCAL_TELEMETRY === 'true';

// Whether ambient telemetry actually ships for this build: always on
// development/production; on local only when explicitly opted in via the flag.
export const telemetryEnabled = !isLocal || localTelemetryEnabled;

// rrweb posts one /track per chunk (no batching with other events). A map,
// calendar, or busy table can emit dozens of chunks on one page. Production
// only, flushed in large chunks so deploy-preview / local never flood the
// ingestion host. Admin keeps this shared 80% sample; customer / partner /
// facility use `openPanelFullSessionReplay` so recordings filter by brand,
// department, or storage facility.
export const openPanelSessionReplay = {
  enabled: isProduction,
  sampleRate: 0.8,
  // No masking, same as the Sentry replay config: a masked recording is not
  // worth recording. On-screen PII (names, phones, plates) and typed input
  // appear in new recordings.
  maskAllText: false,
  maskAllInputs: false,
  flushIntervalMs: 10_000,
  maxEventsPerChunk: 400
} as const;

export const openPanelFullSessionReplay = {
  ...openPanelSessionReplay,
  sampleRate: 1
} as const;

const getEnvFavicon = () =>
  isDevelopment ? '/favicons/navio/dev/favicon.ico' : isLocal ? '/favicons/navio/localhost/favicon.ico' : null;

const getPersistedShowEnvFavicon = (): boolean => {
  try {
    const stored = localStorage.getItem('dev-settings');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.state?.showEnvFavicon ?? true;
    }
  } catch {
    // ignore parse errors
  }
  return true;
};

export const setEnvFavicon = (enabled?: boolean) => {
  const shouldEnable = enabled ?? getPersistedShowEnvFavicon();
  const links = document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']");
  if (links.length === 0) {
    return;
  }

  const href = shouldEnable ? getEnvFavicon() : '/favicon.ico';
  if (!href) {
    return;
  }

  links.forEach((link) => {
    link.href = href;
  });
};
